/* iter120 — Bucket C Field Schedule cleanup.
   Shared preview sheet used everywhere a tech / manager taps a job card on the
   new mobile-first Field Schedule. Renders the same info regardless of where
   the job came from (My Day, Team Day, claimable list) and exposes role-aware
   action buttons:
     • Claim Job        — when the job is claimable (matches /employee/claimable-jobs)
     • Claim Shift      — when the job is mine + scheduled (en-route flow)
     • Update Status    — when the job is mine
     • Complete Job     — when the job is mine + not yet completed
     • (admins/SM see the same actions plus a link to Dispatcher Schedule when on a team-day card)

   The wizard / claim-en-route dialog are still owned by the parent (so this
   sheet remains a pure presentation + action delegator).
*/
import React, { useEffect, useState } from 'react';
import OriginalRecordNote from './OriginalRecordNote';
import axios from 'axios';
import { Button } from './ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from './ui/sheet';
import {
  Clock, Phone, Mail, Users, Hand, ClipboardCheck, Truck,
  AlertTriangle, ExternalLink, DollarSign, FileText, Lock, FilePlus, ClipboardList,
} from 'lucide-react';
import { Link } from 'react-router-dom';
import { Thumbnail } from './LightboxProvider';
import { AddressLink } from './MapsNavProvider';
import JobMediaGallery from './JobMediaGallery';
import PmJobContact from './PmJobContact';
import { fmtLocalTime } from '../lib/timeFmt';
import ScanButton from './inventory/ScanButton';
import { useAuth } from '../contexts/AuthContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// 911-fix-iter172 — Statuses that lock an invoice from tech edits. Office/QB
// touched it, so the tech must request a correction instead of editing
// directly. Admin-tier users (super_admin/admin/manager/service_manager)
// bypass this rule.
// iter175 — Also lock once the HSP claim has been submitted/paid; the
// warranty company has the packet and any edits must go through admin.
const TECH_LOCKED_INVOICE_STATUSES = new Set(['paid', 'refunded', 'partially_refunded', 'void']);
const TECH_LOCKED_QB_STATUSES = new Set(['entered_in_qb', 'in_qb', 'reconciled', 'locked']);
const TECH_LOCKED_HSP_STATUSES = new Set(['submitted_to_hsp', 'partially_paid', 'hsp_paid']);

const fmtTime12 = (hhmm) => {
  if (!hhmm) return '';
  const [h, m] = hhmm.split(':').map(Number);
  const period = h >= 12 ? 'PM' : 'AM';
  return `${h % 12 || 12}:${String(m).padStart(2, '0')} ${period}`;
};
const fmtDateLong = (iso) => {
  if (!iso) return '';
  try {
    const d = new Date(`${iso}T00:00:00`);
    return d.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' });
  } catch { return iso; }
};

const ADMIN_TIER = new Set(['super_admin', 'admin', 'manager', 'service_manager', 'construction_manager', 'office_staff']);

const JobPreviewSheet = ({
  open,
  onOpenChange,
  job,                  // appointment-ish: id, date, start_time, end_time, customer_*, address, dispatcher_notes, notes, photo_urls, status, employee_id, helpers, work_order_number, shift_title, service_type
  origin,               // 'my' | 'team' | 'claim'
  user,
  latestCompletion,
  onClaimJob,           // (job) => Promise — claim an unassigned job (My Day claim-able + Team Day Unassigned)
  onStatusChange,       // (id, newStatus) => Promise
  onClaimShift,         // () => void — opens the en-route ClaimShiftDialog
  onStartJob,           // iter143 — () => Promise — stamps actual_start_time
  onCompleteJob,        // () => void — opens the CompletionWizard
  onCreateEstimate,     // () => void — opens the CompletionWizard on its estimate-builder path
}) => {
  const { hasPermission } = useAuth();
  // 911-fix-iter172 — Pull the invoice group for this shift so we can show
  // "View Invoice / Edit Invoice" instead of "Complete Job" when the job
  // already has at least one invoice on file. Hooks must run before any
  // early return per the rules-of-hooks.
  const [shiftInvoices, setShiftInvoices] = useState([]);
  const [shiftEstimates, setShiftEstimates] = useState([]);
  const jobId = job?.id;
  const isClaimableEarly = !!(origin === 'claim' || (job?.claimable && !job?.employee_id && job?.employee_id !== user?._id));
  useEffect(() => {
    if (!open || !jobId || isClaimableEarly) {
      setShiftInvoices([]);
      setShiftEstimates([]);
      return;
    }
    let cancelled = false;
    axios.get(`${API}/admin/shift-invoices/${jobId}`, { withCredentials: true })
      .then(({ data }) => {
        if (!cancelled) setShiftInvoices(Array.isArray(data?.items) ? data.items : []);
      })
      .catch(() => { if (!cancelled) setShiftInvoices([]); });
    axios.get(`${API}/admin/shift-estimates/${jobId}`, { withCredentials: true })
      .then(({ data }) => {
        if (!cancelled) setShiftEstimates(Array.isArray(data?.items) ? data.items : []);
      })
      .catch(() => { if (!cancelled) setShiftEstimates([]); });
    return () => { cancelled = true; };
  }, [open, jobId, isClaimableEarly]);

  if (!job) return null;

  const isMine = job.employee_id === user?._id;
  const myHelper = (job.helpers || []).find((h) => h.employee_id === user?._id);
  const isHelper = !!myHelper && !isMine;
  const isAdminTier = ADMIN_TIER.has(user?.role);
  const isCompleted = job.status === 'completed';
  const isCancelled = job.status === 'cancelled';
  const isClaimable = origin === 'claim' || (job.claimable && !job.employee_id && !isMine);
  // iter158-cleanup — Internal/no-customer shift (Shop Cleanout, Morning Meeting,
  // Supply Run, etc.). Hide En Route / Claim Shift / customer-SMS language —
  // there's no customer to text or arrive at.
  const isInternalShift = !job.customer_id || job.auto_generated;
  // iter169 — Partial closeout: tech submitted the wizard's first page but never
  // recorded the invoice decision. Anyone who can complete the job (assigned tech,
  // helpers, admin tier) sees a prominent "Finish Billing" button that reopens the
  // Completion Wizard — which itself auto-jumps to the invoice-decision step.
  const needsInvoiceDecision = !!job.needs_invoice_decision && !isCancelled;
  const canFinishBilling = needsInvoiceDecision && (isMine || isHelper || isAdminTier);

  const hasShiftInvoices = shiftInvoices.length > 0;
  const hasShiftEstimates = shiftEstimates.length > 0;
  // Permission-driven (never role-hardcoded) — admins auto-pass inside hasPermission.
  const canCreateInvoice = hasPermission('create_invoices');
  const canCreateEstimate = hasPermission('create_estimates');
  const canViewEstimate = hasPermission('view_estimates');
  const canEditEstimate = hasPermission('edit_estimates');
  // Show the additional-billing actions on any completed job (or a job that already
  // carries billing records), independent of whether an invoice exists yet.
  const showAdditionalActions = !isClaimable && !isCancelled && !needsInvoiceDecision
    && (isCompleted || hasShiftInvoices || hasShiftEstimates);
  const isTechLockedFor = (inv) => {
    if (isAdminTier) return false;
    const s = (inv?.status || '').toLowerCase();
    const qb = (inv?.qb_status || inv?.office_billing_status || '').toLowerCase();
    const hsp = (inv?.hsp_review_status || '').toLowerCase();
    return TECH_LOCKED_INVOICE_STATUSES.has(s) || TECH_LOCKED_QB_STATUSES.has(qb) || TECH_LOCKED_HSP_STATUSES.has(hsp);
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        data-testid="job-preview-sheet"
        className="bg-[#141414] border-l border-[#262626] text-white w-full sm:max-w-md overflow-y-auto"
      >
        <SheetHeader>
          <SheetTitle className="text-white">
            {job.shift_title || job.service_type || 'Job'}
          </SheetTitle>
          <SheetDescription className="sr-only">
            Job preview — customer info, dispatcher notes, and available actions for this shift.
          </SheetDescription>
        </SheetHeader>

        <div className="mt-6 space-y-4">
          {/* Helper badge */}
          {isHelper && (
            <span data-testid="preview-helper-badge" className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-blue-500/15 border border-blue-500/40 text-blue-300 text-[10px] font-bold uppercase tracking-wider">
              You · Helper
            </span>
          )}
          {isMine && !isHelper && origin !== 'claim' && (
            <span data-testid="preview-primary-badge" className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-[#D4AF37]/15 border border-[#D4AF37]/40 text-[#D4AF37] text-[10px] font-bold uppercase tracking-wider">
              You · Primary
            </span>
          )}
          {isClaimable && (
            <span data-testid="preview-claimable-badge" className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-orange-500/15 border border-orange-500/40 text-orange-300 text-[10px] font-bold uppercase tracking-wider ml-2">
              Available to Claim
            </span>
          )}

          {/* iter169 — Partial closeout banner. Surfaces the unfinished invoice
              decision the moment the sheet opens so techs and admins can't miss it. */}
          {needsInvoiceDecision && (
            <div data-testid="preview-finish-billing-banner" className="bg-red-900/20 border border-red-500/50 rounded-md p-3">
              <div className="flex items-start gap-2">
                <DollarSign className="w-4 h-4 text-red-300 flex-shrink-0 mt-0.5" />
                <div className="min-w-0">
                  <p className="text-red-200 text-xs font-bold uppercase tracking-wider">Billing Not Finished</p>
                  <p className="text-white text-sm mt-1">
                    The completion wizard was started but no invoice decision was recorded.
                    {canFinishBilling ? ' Tap Finish Billing to pick the closeout outcome.' : ' The tech or office needs to finish the closeout to clear this job from billing.'}
                  </p>
                </div>
              </div>
            </div>
          )}

          <div>
            <p className="text-[10px] text-[#A3A3A3] uppercase tracking-wider">Service Type</p>
            <p className="text-white">{job.service_type || job.shift_title || '—'}</p>
          </div>
          <OriginalRecordNote visit={job} />

          <div>
            <p className="text-[10px] text-[#A3A3A3] uppercase tracking-wider">Date & Time</p>
            <p className="text-white">{fmtDateLong(job.date)}</p>
            <p className="text-[#D4AF37] font-bold">
              {job.all_day ? 'All day' : `${fmtTime12(job.start_time)} – ${fmtTime12(job.end_time)}`}
            </p>
          </div>

          {/* iter293 — PM relationship: Company → Service Location → Requester.
              Renders only for PM jobs; independent from Bill To. */}
          <PmJobContact job={job} testId="preview-pm-job-contact" />

          {/* Customer Info — same card pattern as iter80 emp-customer-info-card */}
          {(job.customer_name || job.customer_phone || job.customer_email || job.address) && (
            <div data-testid="preview-customer-info" className="bg-[#0A0A0A] border border-[#D4AF37]/40 rounded-md p-3 space-y-3">
              <p className="text-[10px] uppercase tracking-wider text-[#D4AF37] font-bold flex items-center gap-1">
                <Users className="w-3 h-3" /> Customer Info
              </p>
              {job.customer_name && (
                <div>
                  <p className="text-[10px] text-[#737373] uppercase">Name</p>
                  <p data-testid="preview-customer-name" className="text-white">{job.customer_name}</p>
                </div>
              )}
              <div>
                <p className="text-[10px] text-[#737373] uppercase">Phone</p>
                {job.customer_phone ? (
                  <a
                    href={`tel:${(job.customer_phone || '').replace(/[^+\d]/g, '')}`}
                    data-testid="preview-call-link"
                    className="inline-flex items-center gap-2 mt-1 px-3 h-10 rounded-md bg-[#D4AF37] text-[#0A0A0A] font-bold hover:bg-[#F3E5AB] active:bg-[#D4AF37] text-base"
                  >
                    <Phone className="w-4 h-4" />
                    {job.customer_phone}
                    <span className="text-[10px] uppercase ml-1 opacity-70">Tap to Call</span>
                  </a>
                ) : (
                  <p className="text-red-400 text-sm italic mt-1">No phone number on file</p>
                )}
              </div>
              {job.customer_email && (
                <div>
                  <p className="text-[10px] text-[#737373] uppercase">Email</p>
                  <a
                    href={`mailto:${job.customer_email}`}
                    data-testid="preview-email-link"
                    className="text-white text-sm hover:text-[#D4AF37] inline-flex items-center gap-1.5 break-all"
                  >
                    <Mail className="w-3.5 h-3.5 flex-shrink-0" /> {job.customer_email}
                  </a>
                </div>
              )}
              {job.address && (
                <div>
                  <p className="text-[10px] text-[#737373] uppercase">Service Address</p>
                  <AddressLink
                    address={job.address}
                    testId="preview-address-link"
                    className="text-white text-sm"
                    iconClassName="w-3.5 h-3.5 flex-shrink-0"
                  />
                </div>
              )}
            </div>
          )}

          {job.work_order_number && (
            <div>
              <p className="text-[10px] text-[#A3A3A3] uppercase tracking-wider">Work Order #</p>
              <p className="text-white font-mono">{job.work_order_number}</p>
            </div>
          )}

          <JobMediaGallery appointmentId={job.id} workOrderNumber={job.work_order_number} title="Job Photos / Documentation" testId="preview-job-media" />

          {/* Dispatcher (internal) notes — always show when present, regardless of origin */}
          {job.dispatcher_notes && (
            <div data-testid="preview-dispatcher-notes" className="bg-[#D4AF37]/10 border border-[#D4AF37]/30 rounded-md p-3">
              <p className="text-[10px] text-[#D4AF37] uppercase tracking-wider font-bold mb-1">Dispatcher Notes (internal)</p>
              <p className="text-white whitespace-pre-wrap text-sm">{job.dispatcher_notes}</p>
            </div>
          )}

          {/* Customer-facing notes */}
          {job.notes && (
            <div data-testid="preview-customer-notes">
              <p className="text-[10px] text-[#A3A3A3] uppercase tracking-wider">Customer-Facing Notes</p>
              <p className="text-white whitespace-pre-wrap text-sm">{job.notes}</p>
            </div>
          )}

          {/* Photos */}
          {Array.isArray(job.photo_urls) && job.photo_urls.length > 0 && (
            <div data-testid="preview-photos">
              <p className="text-[10px] text-[#A3A3A3] uppercase tracking-wider mb-2">Dispatcher Photos ({job.photo_urls.length})</p>
              <div className="grid grid-cols-3 gap-2">
                {job.photo_urls.map((url, idx) => (
                  <Thumbnail
                    key={idx}
                    src={url}
                    images={job.photo_urls}
                    index={idx}
                    testId={`preview-photo-${idx}`}
                    alt={`Dispatcher photo ${idx + 1}`}
                    className="w-full h-20 object-cover rounded border border-[#262626] hover:border-[#D4AF37]"
                  />
                ))}
              </div>
            </div>
          )}

          {/* Latest completion summary on my own jobs */}
          {origin === 'my' && latestCompletion && (
            <div data-testid="preview-latest-completion" className={`rounded-md p-3 border ${
              latestCompletion.status === 'revisions_requested' ? 'bg-red-900/15 border-red-700/40'
              : latestCompletion.status === 'approved'           ? 'bg-green-900/15 border-green-700/40'
              :                                                    'bg-[#D4AF37]/10 border-[#D4AF37]/40'
            }`}>
              <p className="text-xs uppercase tracking-wider mb-1 font-bold">
                {latestCompletion.status === 'approved'           ? 'Approved'
                : latestCompletion.status === 'revisions_requested' ? 'Revisions Requested'
                :                                                     'Pending Approval'}
              </p>
              {latestCompletion.status === 'revisions_requested' && latestCompletion.revision_note && (
                <p className="text-white text-sm mt-1 whitespace-pre-wrap">
                  <AlertTriangle className="w-4 h-4 inline mr-1 text-red-400" />
                  {latestCompletion.revision_note}
                </p>
              )}
              <p className="text-[#A3A3A3] text-xs mt-1">{(latestCompletion.work_performed || '').slice(0, 140)}</p>
            </div>
          )}

          {/* ─── ACTIONS ─── */}
          <div className="pt-2 border-t border-[#262626] space-y-2">
            {false && job?.id && (   /* Scan Material Used hidden — no field scan-out for now */
              <ScanButton
                mode="job_use"
                label="Scan Material Used"
                testId="preview-scan-material"
                variant="outline"
                className="w-full border-[#D4AF37]/40 text-[#D4AF37] hover:bg-[#D4AF37]/10 h-11"
                context={{ job_id: job.id, appointment_id: job.id, customer_id: job.customer_id || job.customer?.id || null }}
              />
            )}
            {/* iter169 — Finish Billing / Continue Closeout. Visible whenever the
                appointment has a partial closeout (completion submitted but no
                invoice decision), regardless of job.status. Uses the same handler
                as Complete Job because the Completion Wizard's resume logic auto-
                jumps to the invoice-decision step when invoice_status is missing. */}
            {canFinishBilling && (
              <Button
                data-testid="preview-finish-billing"
                onClick={onCompleteJob}
                className="w-full bg-red-600 text-white hover:bg-red-500 font-bold h-12"
              >
                <DollarSign className="w-4 h-4 mr-2" />
                Finish Billing / Continue Closeout
              </Button>
            )}

            {/* 911-fix-iter172 (Issue #2) — Shift already has invoice(s).
                Show View/Edit per invoice (split warranty groups render
                multiple rows). Hides the duplicate-creating Complete Job
                CTA below so a tech can't accidentally start a 2nd closeout. */}
            {hasShiftInvoices && (
              <div data-testid="preview-shift-invoices" className="space-y-1.5">
                <p className="text-[#D4AF37] text-xs uppercase tracking-wider font-bold">
                  {shiftInvoices.length === 1 ? 'Invoice for this shift' : `Invoice Group (${shiftInvoices.length})`}
                </p>
                {shiftInvoices.map((inv) => {
                  const locked = isTechLockedFor(inv);
                  const payor = inv.payor_kind === 'warranty' ? 'Warranty' : (inv.covered_by_membership ? 'Membership' : 'Customer');
                  const amt = typeof inv.amount === 'number' ? `$${inv.amount.toFixed(2)}` : '';
                  return (
                    <div
                      key={inv.id}
                      data-testid={`preview-shift-invoice-${inv.id}`}
                      className="bg-[#0A0A0A] border border-[#262626] rounded p-2.5 flex items-center justify-between gap-2"
                    >
                      <div className="min-w-0">
                        <p className="text-white text-sm font-bold truncate">
                          {inv.invoice_number || `Invoice ${inv.id.slice(-6)}`} <span className="text-[#A3A3A3] font-normal">· {amt} · {payor}</span>
                        </p>
                        <p className="text-[#737373] text-[11px] uppercase tracking-wider">
                          {(inv.status || 'pending').replace(/_/g, ' ')}
                          {inv.office_billing_status && inv.office_billing_status !== 'needs_review' && (
                            <span className="ml-1.5 text-[#D4AF37]">· QB: {inv.office_billing_status.replace(/_/g, ' ')}</span>
                          )}
                          {locked && (
                            <span className="ml-1.5 text-amber-300 inline-flex items-center gap-0.5">
                              <Lock className="w-3 h-3" /> Locked
                            </span>
                          )}
                        </p>
                      </div>
                      <div className="flex items-center gap-1">
                        <Link
                          to={`/admin/invoices?status=all&open=${inv.id}`}
                          data-testid={`preview-invoice-view-${inv.id}`}
                          className="inline-flex items-center gap-1 px-2 py-1 rounded bg-[#262626] hover:bg-[#3a3a3a] text-white text-xs font-bold"
                        >
                          <FileText className="w-3.5 h-3.5" /> View
                        </Link>
                        {!locked && (
                          <Link
                            to={`/admin/invoices?status=all&open=${inv.id}&edit=1`}
                            data-testid={`preview-invoice-edit-${inv.id}`}
                            className="inline-flex items-center gap-1 px-2 py-1 rounded bg-[#D4AF37] text-[#0A0A0A] hover:bg-[#F3E5AB] text-xs font-bold"
                          >
                            Edit
                          </Link>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

            {/* ─── ESTIMATE GROUP (mirrors Invoice Group) ─── */}
            {hasShiftEstimates && (
              <div data-testid="preview-shift-estimates" className="space-y-1.5">
                <p className="text-blue-300 text-xs uppercase tracking-wider font-bold flex items-center gap-1">
                  <ClipboardList className="w-3.5 h-3.5" />
                  {shiftEstimates.length === 1 ? 'Estimate for this shift' : `Estimate Group (${shiftEstimates.length})`}
                </p>
                {shiftEstimates.map((est) => {
                  const amt = typeof est.amount === 'number' ? `$${est.amount.toFixed(2)}` : '';
                  const st = (est.workflow_status || est.status || 'draft').replace(/_/g, ' ');
                  return (
                    <div
                      key={est.id}
                      data-testid={`preview-shift-estimate-${est.id}`}
                      className="bg-[#0A0A0A] border border-[#262626] rounded p-2.5 flex items-center justify-between gap-2"
                    >
                      <div className="min-w-0">
                        <p className="text-white text-sm font-bold truncate">
                          {est.estimate_number || `Estimate ${est.id.slice(-6)}`} <span className="text-[#A3A3A3] font-normal">· {amt}</span>
                        </p>
                        <p className="text-[#737373] text-[11px] uppercase tracking-wider">{st}</p>
                      </div>
                      <div className="flex items-center gap-1">
                        {canViewEstimate && (
                          <Link
                            to={`/admin/estimates?open=${est.id}`}
                            data-testid={`preview-estimate-view-${est.id}`}
                            className="inline-flex items-center gap-1 px-2 py-1 rounded bg-[#262626] hover:bg-[#3a3a3a] text-white text-xs font-bold"
                          >
                            <FileText className="w-3.5 h-3.5" /> View
                          </Link>
                        )}
                        {canEditEstimate && (
                          <Link
                            to={`/admin/estimates?open=${est.id}&edit=1`}
                            data-testid={`preview-estimate-edit-${est.id}`}
                            className="inline-flex items-center gap-1 px-2 py-1 rounded bg-[#D4AF37] text-[#0A0A0A] hover:bg-[#F3E5AB] text-xs font-bold"
                          >
                            Edit
                          </Link>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

            {/* ─── ADDITIONAL BILLING ACTIONS (permission-driven) ─── */}
            {showAdditionalActions && (canCreateInvoice || canCreateEstimate) && (
              <div data-testid="preview-additional-billing" className="space-y-2 pt-1">
                {canCreateInvoice && (
                  <Button
                    data-testid="preview-create-additional-invoice"
                    onClick={onCompleteJob}
                    variant="outline"
                    className="w-full border-[#D4AF37]/40 text-[#D4AF37] hover:bg-[#D4AF37]/10 h-11"
                  >
                    <FilePlus className="w-4 h-4 mr-2" /> Create Additional Invoice
                  </Button>
                )}
                {canCreateEstimate && (
                  <Button
                    data-testid="preview-create-additional-estimate"
                    onClick={onCreateEstimate}
                    variant="outline"
                    className="w-full border-blue-400/40 text-blue-300 hover:bg-blue-500/10 h-11"
                  >
                    <ClipboardList className="w-4 h-4 mr-2" /> Create Additional Estimate
                  </Button>
                )}
              </div>
            )}

            {/* Claim Job — claimable origin OR team-day unassigned */}
            {isClaimable && (
              <Button
                data-testid="preview-claim-job"
                onClick={() => onClaimJob && onClaimJob(job)}
                className="w-full bg-[#D4AF37] text-[#0A0A0A] hover:bg-[#F3E5AB] font-bold h-12"
              >
                <Hand className="w-4 h-4 mr-2" /> Claim Job
              </Button>
            )}

            {/* My job — Status + Claim Shift (en-route) + Complete */}
            {isMine && !isCompleted && !isCancelled && (
              <>
                <div>
                  <p className="text-[10px] text-[#A3A3A3] uppercase tracking-wider mb-1">Update Status</p>
                  <Select value={job.status} onValueChange={(v) => onStatusChange && onStatusChange(job.id, v)}>
                    <SelectTrigger data-testid="preview-status" className="bg-[#0A0A0A] border-[#262626] text-white">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-[#141414] border-[#262626]">
                      <SelectItem value="scheduled"   className="text-white">Scheduled</SelectItem>
                      <SelectItem value="in_progress" className="text-white">In Progress</SelectItem>
                      <SelectItem value="cancelled"   className="text-white">Cancelled</SelectItem>
                    </SelectContent>
                  </Select>
                  <p className="text-[#737373] text-[11px] mt-2">To mark complete, use Complete Job below — goes to your manager for approval.</p>
                </div>

                {/* iter158-cleanup — Hide "Claim Shift (en route)" on internal shifts. */}
                {job.status === 'scheduled' && !isInternalShift && (
                  <Button
                    data-testid="preview-claim-shift"
                    onClick={onClaimShift}
                    className="w-full bg-blue-600 text-white hover:bg-blue-500"
                  >
                    <Truck className="w-4 h-4 mr-2" /> Claim Shift (en route)
                  </Button>
                )}

                {/* iter158-cleanup — Internal-shift start button (no customer, no SMS). */}
                {isInternalShift && !job.actual_start_time && job.status !== 'completed' && (
                  <Button
                    data-testid="preview-start-internal-shift"
                    onClick={onStartJob}
                    className="w-full bg-blue-600 text-white hover:bg-blue-500 h-11"
                  >
                    <Clock className="w-4 h-4 mr-2" /> Start Shift
                  </Button>
                )}

                {/* iter143 — Start Job / Arrived stamps actual_start_time. Visible after the
                    tech has claimed the shift but before they've started work. Hides itself
                    once actual_start_time is set so it can't be tapped twice.
                    iter158-cleanup — Hidden on internal shifts (handled above). */}
                {!isInternalShift && onStartJob && !job.actual_start_time && job.claimed_at && job.status !== 'completed' && (
                  <Button
                    data-testid="preview-start-job"
                    onClick={onStartJob}
                    className="w-full bg-green-600 text-white hover:bg-green-500 h-11"
                  >
                    <Clock className="w-4 h-4 mr-2" /> Start Job / Arrived
                  </Button>
                )}
                {job.actual_start_time && job.status !== 'completed' && (
                  <p data-testid="preview-on-job-indicator" className="text-blue-300 text-sm font-bold text-center bg-blue-500/10 border border-blue-500/30 rounded px-2 py-2">
                    ON JOB since {fmtLocalTime(job.actual_start_time)}
                  </p>
                )}

                {/* iter144 — Surface actual_end_time + duration the moment a job is marked
                    done (independent of approval workflow). Shown alongside the existing
                    on-job-indicator so the detail sheet stays consistent with the schedule
                    card tags. The original arrival window above (Date & Time) is untouched. */}
                {job.actual_end_time && (
                  <p data-testid="preview-done-indicator" className="text-green-300 text-sm font-bold text-center bg-green-500/10 border border-green-500/30 rounded px-2 py-2">
                    DONE at {fmtLocalTime(job.actual_end_time)}
                    {Number.isFinite(job.actual_duration_minutes) && job.actual_duration_minutes > 0 && (
                      <span className="text-green-200/80 font-normal"> · {(job.actual_duration_minutes / 60).toFixed(2)}h on job</span>
                    )}
                  </p>
                )}

                {latestCompletion?.status !== 'pending_approval' && !hasShiftInvoices && (
                  <Button
                    data-testid="preview-complete-job"
                    onClick={onCompleteJob}
                    className="w-full bg-[#D4AF37] text-[#0A0A0A] hover:bg-[#F3E5AB] font-bold h-12"
                  >
                    <ClipboardCheck className="w-4 h-4 mr-2" />
                    {latestCompletion?.status === 'revisions_requested'
                      ? 'Resubmit Completion'
                      : (isInternalShift ? 'Complete Shift' : 'Complete Job')}
                  </Button>
                )}
              </>
            )}

            {/* Admin / SM viewing a team-day card — quick link to dispatcher schedule */}
            {origin === 'team' && !isMine && isAdminTier && (
              <Link
                to="/admin/schedule"
                data-testid="preview-open-dispatcher"
                className="inline-flex items-center gap-2 text-[#D4AF37] hover:underline text-sm"
                onClick={() => onOpenChange(false)}
              >
                <ExternalLink className="w-4 h-4" /> Open in Dispatcher Schedule
              </Link>
            )}

            {/* Completed / cancelled — no actions */}
            {(isCompleted || isCancelled) && (
              <>
                {/* iter144 — Even on completed jobs surface the actual end time + duration
                    so the detail sheet matches the schedule card's DONE tag. */}
                {isCompleted && job.actual_end_time && (
                  <p data-testid="preview-done-indicator" className="text-green-300 text-sm font-bold text-center bg-green-500/10 border border-green-500/30 rounded px-2 py-2">
                    DONE at {fmtLocalTime(job.actual_end_time)}
                    {Number.isFinite(job.actual_duration_minutes) && job.actual_duration_minutes > 0 && (
                      <span className="text-green-200/80 font-normal"> · {(job.actual_duration_minutes / 60).toFixed(2)}h on job</span>
                    )}
                  </p>
                )}
                <p className="text-[#737373] text-xs italic">
                  <Clock className="w-3 h-3 inline mr-1" />
                  Job is {job.status.replace('_', ' ')}. No actions available.
                </p>
              </>
            )}
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
};

export default JobPreviewSheet;
